<template>
  <v-row>
    <v-col
      v-if="!authorizationUtils.isClient()"
      cols="12"
      sm="6"
      md="4"
      class="mb-3"
    >
      <v-autocomplete
        filled
        v-model="searchParams.clientId"
        :items="clients"
        item-text="name"
        item-value="id"
        hide-details="auto"
        clearable
        :loading="loading.clients"
        :search-input.sync="search.clients"
        :placeholder="$t('defaults.remote-dropdown-placeholder')"
        hide-no-data
        :label="$t('received-docs.search.form.client')"
      >
        <template v-slot:item="data">
          <v-icon
            :color="data.item.isActive ? 'success' : 'error'"
            size="12"
          >
            mdi-circle
          </v-icon>
          <span class="ms-2">
            {{ data.item.name }}
          </span>
        </template>
      </v-autocomplete>
    </v-col>
    <v-col cols="12" sm="6" md="4" class="mb-3">
      <v-text-field
        :label="$t('received-docs.search.form.document-name')"
        v-model.trim="searchParams.displayName"
        hide-details="auto"
        filled
        type="text"
      ></v-text-field>
    </v-col>
    <v-col cols="12" sm="6" md="4" class="mb-3">
      <BaseTagsDropdown
        v-model="searchParams.tags"
        :items="tags"
        :label="$t('received-docs.search.form.tags')"
        :loading="loading.others"
      ></BaseTagsDropdown>
    </v-col>
    <v-col
      v-if="!authorizationUtils.isClient()"
      cols="12"
      sm="6"
      md="4"
      class="mb-3"
    >
      <v-select
        filled
        v-model="searchParams.status"
        :items="statuses"
        item-text="displayText"
        item-value="value"
        hide-details="auto"
        clearable
        :label="$t('received-docs.search.form.status.title')"
      ></v-select>
    </v-col>
    <v-col cols="12" sm="6" md="4" class="mb-3">
      <v-autocomplete
        filled
        v-model="searchParams.serviceId"
        :items="services"
        item-text="displayName"
        item-value="id"
        hide-details="auto"
        clearable
        :label="$t('received-docs.search.form.service')"
      ></v-autocomplete>
    </v-col>
    <v-col cols="12" sm="6" md="4" class="mb-3">
      <v-menu
        :close-on-content-click="false"
        :nudge-right="40"
        transition="scale-transition"
        offset-y
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            v-model="selectedDateText"
            :label="$t('received-docs.search.form.upload-date')"
            readonly
            v-bind="attrs"
            v-on="on"
            hide-details="auto"
            filled
            clearable
            @click:clear="clearSelectedDate"
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="selectedDateRange"
          range
          no-title
        ></v-date-picker>
      </v-menu>
    </v-col>
    <v-col cols="12" sm="6" md="4" class="mb-3">
      <v-autocomplete
        filled
        v-model="searchParams.creatorUserId"
        :items="filteredUsers"
        item-text="displayText"
        item-value="value"
        hide-details="auto"
        :loading="loading.users"
        :search-input.sync="search.users"
        :placeholder="$t('defaults.remote-dropdown-placeholder')"
        hide-no-data
        clearable
        :label="$t('received-docs.search.form.uploaded-by')"
      ></v-autocomplete>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import authorizationUtils from "@/utils/authorization.js";
import BaseTagsDropdown from "@/components/BaseTagsDropdown.vue";

export default {
  components: {
    BaseTagsDropdown
  },
  props: {
    services: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      authorizationUtils,
      search: {
        clients: null,
        users: null
      },
      loading: {
        clients: false,
        others: false,
        users: false
      },
      selectedDateText: null,
      selectedDateRange: null,
      clients: [],
      tags: [],
      users: [],
      filteredUsers: [],
      statuses: [
        {
          value: 1,
          displayText: this.$t("received-docs.search.form.status.data.open")
        },
        {
          value: 2,
          displayText: this.$t(
            "received-docs.search.form.status.data.in-progress"
          )
        },
        {
          value: 3,
          displayText: this.$t("received-docs.search.form.status.data.done")
        },
        {
          value: null,
          displayText: this.$t("received-docs.search.form.status.data.all")
        }
      ]
    };
  },
  watch: {
    selectedDateRange: {
      handler(range) {
        if (range) {
          const sortedRange = range
            .map((element) => {
              var d = new Date(element);
              return `${
                d.getUTCMonth() + 1
              }-${d.getUTCDate()}-${d.getUTCFullYear()}`;
            })
            .sort(function(a, b) {
              return new Date(a).getTime() - new Date(b).getTime();
            });

          [
            this.searchParams.creationTimeFrom,
            this.searchParams.creationTimeTo
          ] = sortedRange;

          this.selectedDateText = sortedRange.join(" ~ ");
        }
      },
      immediate: true
    },
    searchParams: {
      handler() {
        this.setSearchParams(this.searchParams);
      },
      immediate: true,
      deep: true
    },
    "searchParams.creationTimeFrom": {
      handler(val) {
        if (
          !val &&
          !this.setSearchParams.creationTimeTo &&
          this.selectedDateRange != null
        ) {
          this.selectedDateRange = null;
          this.selectedDateText = null;
        }
      },
      immediate: true,
      deep: true
    },
    "search.clients": {
      handler(val) {
        if (val?.length > 2) {
          this.fetchAllClients(val);
        } else {
          this.clients = [];
        }
      }
    },
    "search.users": {
      handler(val) {
        if (val?.length > 2) {
          this.filterUsers(val);
        } else {
          this.filteredUsers = JSON.parse(JSON.stringify(this.users));
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      searchParams: "SearchParams/getSearchParams"
    })
  },
  methods: {
    ...mapActions({
      setSearchParams: "SearchParams/setSearchParams"
    }),
    fetchAllTags() {
      return window.API.fetchAllTags()
        .then((response) => {
          this.tags = response;
        })
        .catch(() => this.showFetchRequestErrorMessage());
    },
    fetchAllNonClientUsers() {
      return window.API.fetchAllNonClientUsers()
        .then((response) => {
          this.users = response;
          this.filteredUsers = response;
        })
        .catch(() => this.showFetchRequestErrorMessage());
    },
    fetchClientUsers(searchTerm) {
      this.loading.users = true;

      window.API.fetchClientUsers(searchTerm)
        .then((response) => {
          this.filteredUsers = response;
        })
        .catch(() => this.showFetchRequestErrorMessage())
        .finally(() => (this.loading.users = false));
    },
    fetchAllClients(searchTerm) {
      this.loading.clients = true;

      window.API.fetchAllClients(searchTerm)
        .then((response) => {
          this.clients = response;
        })
        .catch(() => this.showFetchRequestErrorMessage())
        .finally(() => (this.loading.clients = false));
    },
    filterUsers(searchTerm) {
      if (authorizationUtils.isClient()) return;

      this.fetchClientUsers(searchTerm);
    },
    clearSelectedDate() {
      this.selectedDateRange = null;
      this.searchParams.creationTimeFrom = null;
      this.searchParams.creationTimeTo = null;
    }
  },
  async mounted() {
    this.loading.others = true;

    try {
      await this.fetchAllTags();

      if (authorizationUtils.isClient()) {
        await this.fetchAllNonClientUsers();
      }
    } finally {
      this.loading.others = false;
    }
  }
};
</script>
